import React, { useState, useEffect, useCallback } from "react";
import Add_Team from "./Add_Team";
import Pagination from "@/components/Pagination";
import SearchInput from "@/components/SearchBar";
import { usegetTeam } from "@/hooks/useTenantDashboardService";
import { API_BASE_URL_IMAGE } from "@/config/api.enpoints";
import { useUserStore } from "@/store";
import { useDashboardTheme } from "../MeetingDashboard/themeHelper";
import { Pencil } from "lucide-react";
import { IconButton } from "@mui/material";
import { useDebounce } from "@/utils/useDebounce";

interface UserType {
  id: string;
  name: string;
  email: string;
  role: string;
  userImg?: string;
  isVerified: boolean;
  status: boolean;
  companyName?: string;
  phoneNumber?: string;
  memberCount?: number;
  members: {
    name: string;
  }[];
}

const Teams = () => {
  const { isDark, colors } = useDashboardTheme();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isAddingUser, setIsAddingUser] = useState<boolean>(false);
  const itemsPerPage = 10;

  const debouncedSearchTerm = useDebounce(searchTerm, 800);

  const { editUserData, setEditUserData, setPageHeader } = useUserStore();

  const {
    data: teamResponse,
    refetch,
    isLoading,
  } = usegetTeam(currentPage, itemsPerPage, debouncedSearchTerm);
  const teams: UserType[] = teamResponse?.data?.data ?? [];
  const totalPages = teamResponse?.data?.totalPages ?? 1;

  const headingText = "Teams";

  useEffect(() => {
    setPageHeader(headingText);
  }, [setPageHeader]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value),
    [],
  );

  const handleCloseAddUser = () => {
    setIsAddingUser(false);
    setEditUserData(null);
    refetch();
  };

  useEffect(() => {
    if (editUserData && !isAddingUser) {
      setIsAddingUser(true);
    }
  }, [editUserData, isAddingUser]);

  return (
    <>
      <div className="flex justify-between items-center px-6 mt-25">
        <SearchInput
          placeholder="Search here..."
          value={searchTerm}
          onChange={handleSearch}
        />
        <button
          className="px-5 py-2.5 font-bold poppins rounded-full cursor-pointer transition-all duration-300 shadow-md active:scale-95 text-sm"
          style={{
            background: isDark ? "#B8F890" : "#5a9d50",
            color: isDark ? "#000000" : "#ffffff",
          }}
          onClick={() => {
            setIsAddingUser(true);
            setEditUserData(null);
            refetch();
          }}
        >
          Add New Team
        </button>
      </div>

      <main className="flex-1 p-2">
        <div className="min-h-[75vh] rounded-xl p-6 mx-auto flex flex-col gap-6 shadow-lg">
          {isAddingUser && (
            <Add_Team
              onClose={handleCloseAddUser}
              open={isAddingUser}
              editUserData={
                editUserData
                  ? {
                      ...editUserData,
                      companyName: editUserData.companyName ?? "",
                      phoneNumber: editUserData.phoneNumber ?? "",
                      id: editUserData.id ?? "",
                    }
                  : null
              }
            />
          )}
          <div className="h-full rounded-lg overflow-hidden">
            {isLoading ? (
              <div className="text-center py-10" style={{ color: colors.textMuted }}>Loading...</div>
            ) : teams.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {teams.map((u: UserType, idx: number) => (
                  <div
                    key={idx}
                    className="rounded-2xl p-6 transition-all duration-300 shadow-md flex flex-col justify-between"
                    style={{
                      background: colors.surface,
                      border: `1px solid ${colors.border}`,
                      color: colors.text,
                    }}
                  >
                    <div className="flex justify-between items-start mb-6">
                      <div className="flex items-center gap-4">
                        {u.userImg ? (
                          <img
                            src={
                              u.userImg.startsWith("http://") || u.userImg.startsWith("https://")
                                ? u.userImg
                                : API_BASE_URL_IMAGE + u.userImg
                            }
                            alt={u.name}
                            className="w-12 h-12 rounded-full object-cover border-2"
                            style={{ borderColor: colors.border }}
                          />
                        ) : (
                          <div
                            className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg"
                            style={{
                              background: isDark ? "rgba(184, 248, 144, 0.15)" : "rgba(90, 157, 80, 0.15)",
                              color: isDark ? "#B8F890" : "#5a9d50",
                            }}
                          >
                            {u.name
                              ?.split(" ")
                              .map((p: string) => p[0])
                              .join("")
                              .toUpperCase()
                              .slice(0, 2)}
                          </div>
                        )}
                        <div>
                          <h3 className="text-xl font-semibold tracking-wide" style={{ color: colors.text }}>{u.name}</h3>
                        </div>
                      </div>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditUserData({
                            ...u,
                            status: u.status ? "active" : "inactive",
                            role: u.role === "ADMIN" ? "ADMIN" : "USER",
                            companyName: u.companyName ?? "",
                            phoneNumber: u.phoneNumber ?? "",
                          });
                          setIsAddingUser(true);
                        }}
                        sx={{ color: isDark ? "#38bdf8" : "#0284c7" }}
                        title="Edit Team"
                      >
                        <Pencil size={18} />
                      </IconButton>
                    </div>

                    <div className="mb-6">
                      <h4 className="text-xs uppercase tracking-wider font-semibold mb-3" style={{ color: colors.textMuted }}>Members</h4>
                      {Array.isArray(u?.members) && u.members.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {u.members.slice(0, 5).map((m, i) => (
                            <span
                              key={i}
                              className="px-3 py-1 text-xs rounded-full font-medium"
                              style={{
                                background: colors.surfaceAlt,
                                border: `1px solid ${colors.border}`,
                                color: colors.text,
                              }}
                            >
                              {m.name}
                            </span>
                          ))}
                          {u.members.length > 5 && (
                            <span
                              className="px-3 py-1 text-xs rounded-full font-medium"
                              style={{
                                background: colors.surfaceAlt,
                                border: `1px solid ${colors.border}`,
                                color: colors.textMuted,
                              }}
                            >
                              +{u.members.length - 5} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm italic" style={{ color: colors.textMuted }}>No Members</span>
                      )}
                    </div>

                    <div className="flex justify-between items-center pt-4" style={{ borderTop: `1px solid ${colors.border}` }}>
                      <span className="text-sm font-medium" style={{ color: colors.textMuted }}>Total Count</span>
                      <span
                        className="font-bold px-3 py-1 rounded-full text-sm"
                        style={{
                          background: isDark ? "#B8F890" : "#5a9d50",
                          color: isDark ? "#000000" : "#ffffff",
                        }}
                      >
                        {u.memberCount ?? 0}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="text-center py-20 rounded-xl"
                style={{
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  color: colors.textMuted,
                }}
              >
                No Teams Found. Create one to get started!
              </div>
            )}
          </div>

          {!isAddingUser && totalPages > 1 && (
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          )}
        </div>
      </main>
    </>
  );
};

export default Teams;
